import type { Book } from "../data/books";
import type { Model } from "../data/models";
import { LunarMotif, HanukkahMotif, DiwaliMotif, FriendsMotif } from "./motifs";

type Props = {
  book: Book;
  name?: string;
  model?: Model | undefined;
};

export function BookCover({ book, name, model }: Props) {
  const { cover, titleSecond, namePrefix, titleLines } = book;
  const fallbackName = book.placeholderName !== undefined ? book.placeholderName : "Your Child";
  const displayName = (name && name.trim()) || fallbackName;
  const safeName = displayName.length > 12 ? displayName.slice(0, 12) + "…" : displayName;
  const showName = !!safeName;

  return (
    <svg
      viewBox="0 0 300 300"
      preserveAspectRatio="xMidYMid slice"
      className="cover"
      style={{ width: "100%", height: "100%" }}
    >
      <defs>
        <pattern id={`grain-${book.id}`} patternUnits="userSpaceOnUse" width="3" height="3">
          <rect width="3" height="3" fill={cover.bg} />
          <circle cx="1" cy="1" r="0.3" fill="rgba(0,0,0,0.05)" />
        </pattern>
        <linearGradient id={`grad-${book.id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={cover.bg} stopOpacity="0.1" />
          <stop offset="1" stopColor="#000" stopOpacity="0.25" />
        </linearGradient>
      </defs>

      <rect width="300" height="300" fill={`url(#grain-${book.id})`} />
      <rect x="14" y="14" width="272" height="272" fill="none" stroke={cover.accent} strokeWidth="1.5" opacity="0.55" />

      {book.id === "lunar" && <LunarMotif c={cover} model={model} />}
      {book.id === "hanukkah" && <HanukkahMotif c={cover} model={model} />}
      {book.id === "diwali" && <DiwaliMotif c={cover} model={model} />}
      {book.id === "friends" && <FriendsMotif c={cover} model={model} />}

      <g transform={`translate(150, ${titleLines ? 205 : 218})`}>
        {showName && (
          <text textAnchor="middle" fill={cover.ink} fontFamily="Jost, ui-sans-serif, system-ui, sans-serif" fontSize="22" letterSpacing="-0.5">
            {safeName}
          </text>
        )}
        {namePrefix && (
          <text
            y="18"
            textAnchor="middle"
            fill={cover.ink}
            opacity="0.85"
            fontFamily="Jost, ui-sans-serif, system-ui, sans-serif"
            fontSize="12"
          >
            {namePrefix}
          </text>
        )}
        {titleLines ? (
          <g>
            <text
              y={showName ? (namePrefix ? 42 : 28) : 0}
              textAnchor="middle"
              fill={cover.accent}
              fontFamily="Jost, ui-sans-serif, system-ui, sans-serif"
              fontSize="18"
              letterSpacing="-0.3"
            >
              {titleLines[0]}
            </text>
            <text
              y={(showName ? (namePrefix ? 42 : 28) : 0) + 22}
              textAnchor="middle"
              fill={cover.accent}
              fontFamily="Jost, ui-sans-serif, system-ui, sans-serif"
              fontSize="18"
              letterSpacing="-0.3"
            >
              {titleLines[1]}
            </text>
          </g>
        ) : (
          <text
            y={showName ? (namePrefix ? 42 : 28) : 0}
            textAnchor="middle"
            fill={cover.accent}
            fontFamily="Jost, ui-sans-serif, system-ui, sans-serif"
            fontSize="19"
            letterSpacing="-0.3"
          >
            {titleSecond}
          </text>
        )}
      </g>

      <text
        x="150"
        y="282"
        textAnchor="middle"
        fill={cover.ink}
        opacity="0.6"
        fontFamily="Jost, ui-sans-serif"
        fontSize="7"
        letterSpacing="2"
      >
        HUSHBERRY · NEW YORK
      </text>

      <rect width="300" height="300" fill={`url(#grad-${book.id})`} opacity="0.6" pointerEvents="none" />
    </svg>
  );
}
